// src/features/leases/leaseTypes.js
//
// LSE-AUDIT-1 Part 3 — single source of truth for the 4 canonical lease
// types. DB values are snake_case (migration 0022 expanded the CHECK
// constraint; 0023 tightened it). UI displays the short labels from the
// brief. EditableLeaseField's <select> reads {value,label} objects and
// submits `value` to the server, so the dropdown automatically PATCHes
// the canonical DB form.
//
// Operational/Retired classification:
//   - "operational" if the location has at least one ACTIVE lease whose
//     type indicates Ripple OCCUPIES the space (direct_lease,
//     sublease_inbound, serviced_office). sublease_outbound means Ripple
//     is the landlord on a space we no longer use → not occupying.
//   - "retired" if the location has leases but none are
//     active-occupying.
//   - null if the location has zero leases (different visual state per
//     the brief — render no badge at all, not a "retired" one).

export const LEASE_TYPE_OPTIONS = Object.freeze([
  Object.freeze({ value: "direct_lease",      label: "Direct Lease" }),
  Object.freeze({ value: "sublease_inbound",  label: "Sublease (In)" }),
  Object.freeze({ value: "sublease_outbound", label: "Sublease (Out)" }),
  Object.freeze({ value: "serviced_office",   label: "Serviced Office" }),
]);

const LABEL_BY_VALUE = Object.freeze(
  Object.fromEntries(LEASE_TYPE_OPTIONS.map((o) => [o.value, o.label])),
);

export const LEASE_TYPE_VALUES = Object.freeze(
  LEASE_TYPE_OPTIONS.map((o) => o.value),
);

/**
 * Return the short display label for a canonical lease type value.
 * Unknown values fall through unchanged (defensive — surfaces drift
 * rather than hiding it).
 */
export function leaseTypeLabel(value) {
  if (value == null || value === "") return null;
  return LABEL_BY_VALUE[value] ?? value;
}

const ACTIVE_OCCUPYING_TYPES = Object.freeze(
  new Set(["direct_lease", "sublease_inbound", "serviced_office"]),
);

/**
 * Classify the location's occupancy status from its leases array.
 *
 * Returns:
 *   - "operational" : at least one active lease whose type indicates
 *                     Ripple occupies the space
 *   - "retired"     : the location has leases, but none are
 *                     active-occupying
 *   - null          : no leases at all → caller should render no badge
 */
export function locationOccupancyStatus(leases) {
  if (!Array.isArray(leases) || leases.length === 0) return null;
  const occupying = leases.some(
    (l) =>
      l &&
      l.status === "active" &&
      ACTIVE_OCCUPYING_TYPES.has(l.type),
  );
  return occupying ? "operational" : "retired";
}
